import { FocusTrap } from 'focus-trap-react';
import * as React from 'react';
import { CSSTransition } from 'react-transition-group';

import { Portal } from '@xing-com/portal';

import { StatusBannerPrimitive } from './status-banner.primitive';
import {
  statusBannerDuration,
  statusBannerTransitionName,
} from './status-banner.styles';
import type { StatusBannerProps } from './status-banner.types';

export const StatusBanner = React.forwardRef<HTMLDivElement, StatusBannerProps>(
  (
    {
      children,
      className,
      closeButtonProps,
      display = 'fixed',
      focusTrapProps,
      handleOnClose,
      onExited,
      show = true,
      showCloseButton = true,
      variant,
      ...props
    },
    forwardedRef
  ): JSX.Element => {
    const bannerRef = React.useRef<HTMLDivElement>(null);

    const combinedRef = React.useMemo(() => {
      if (forwardedRef) {
        return typeof forwardedRef === 'function'
          ? { current: null, set: forwardedRef }
          : forwardedRef;
      }
      return bannerRef;
    }, [forwardedRef]);

    const timeout = display === 'fixed' ? parseInt(statusBannerDuration) : 0;

    const {
      focusTrapOptions: userFocusTrapOptions,
      active: userActive,
      ...otherFocusTrapProps
    } = focusTrapProps || {};
    const mergedFocusTrapOptions = {
      allowOutsideClick: true,
      escapeDeactivates: true,
      fallbackFocus: () => combinedRef.current!,
      returnFocusOnDeactivate: true,
      ...userFocusTrapOptions,
    };

    // Focus trap is disabled by default. Opt in via focusTrapProps.active
    const focusTrapActive = userActive ?? false;

    const statusBanner = (
      <StatusBannerPrimitive
        className={className}
        closeButtonProps={closeButtonProps}
        display={display}
        handleOnClose={handleOnClose}
        ref={combinedRef}
        showCloseButton={showCloseButton}
        tabIndex={-1}
        variant={variant}
        {...props}
      >
        {children}
      </StatusBannerPrimitive>
    );

    const statusBannerTransition = (
      <CSSTransition
        appear={show}
        classNames={statusBannerTransitionName}
        in={show}
        key="statusbanner"
        mountOnEnter
        onExited={onExited}
        timeout={timeout}
        unmountOnExit
        nodeRef={combinedRef}
      >
        <FocusTrap
          active={focusTrapActive && show && display === 'fixed'}
          {...otherFocusTrapProps}
          focusTrapOptions={mergedFocusTrapOptions}
        >
          {statusBanner}
        </FocusTrap>
      </CSSTransition>
    );

    if (display === 'fixed') {
      return <Portal>{statusBannerTransition}</Portal>;
    }

    return statusBannerTransition;
  }
);

StatusBanner.displayName = 'StatusBanner';
